import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Image,
  Link as ChakraLink,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
// @ts-ignore
import AppVerify from '../../../assets/icons/AppVerify.svg';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { getAppDetails } from '../../../redux/appBuilder/appDetails/selectors';
import {
  updateAppActiveStep,
  updateAppVerified,
} from '../../../redux/appBuilder/stepperDetails';
import {
  getAppActiveStep,
  getAppType,
} from '../../../redux/appBuilder/stepperDetails/selectors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getAppById } from '../../../utils/app-builder/fetch-applications';

// Add this function at the top of the file or just before the component
const getDocumentationUrl = (appType: string, appPlatform: string) => {
  switch (appType) {
    case 'WEB':
      return 'https://docs.reclaimprotocol.org/js';
    case 'MOBILE':
      return appPlatform === 'FLUTTER'
        ? 'https://docs.reclaimprotocol.org/flutter'
        : 'https://docs.reclaimprotocol.org/react-native';
    case 'ZK_FETCH':
      return 'https://gitlab.reclaimprotocol.org/integrations/offchain/zk-fetch';
    default:
      return 'https://docs.reclaimprotocol.org/';
  }
};

export const VerifyApp = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const appData = useAppSelector(getAppDetails);
  const appType = useAppSelector(getAppType);
  const activeStep = useAppSelector(getAppActiveStep);
  const { width } = useWindowDimensions();
  const [gotSuccessFullProof, setGotSuccessFullProof] = useState(false);

  const fetchAppDetails = async () => {
    // fetch app details
    const data = await getAppById(appData.appId as string);
    if (data?.isSuccess) {
      if (data.application.successSessions) {
        setGotSuccessFullProof(true);
      }
    } else {
      console.log('Failed to fetch application');
    }
  };

  const handleBackAction = () => {
    dispatch(updateAppActiveStep(3));
    navigate(`/my-applications/code-preview/${appData.appId}`);
  };

  useEffect(() => {
    // fetch app details in 5 sec interval until gotSuccessFullProof is true
    const interval = setInterval(() => {
      fetchAppDetails();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (gotSuccessFullProof) {
      dispatch(updateAppVerified(true));
      dispatch(updateAppActiveStep(activeStep + 1));
    }
  }, [gotSuccessFullProof]);

  return (
    <Card
      w={width > 1740 ? '1300px' : '95%'}
      mt={6}
      variant="outline"
      borderRadius="12px"
      h="fit-content"
    >
      <CardBody p={0}>
        <VStack
          align="flex-start"
          spacing={5}
          padding={7}
          pb={0}
          h="72vh"
          overflowY="scroll"
        >
          <VStack w="100%" alignItems="center">
            <Text
              fontSize="xxx-large"
              mt="6vh"
              color="black"
              fontWeight="bold"
              fontFamily="inter"
            >
              Did it work yet?
            </Text>
            <Text className="x-small-font" color="gray" fontFamily="inter">
              Check out the {/* underline docs */}
              <ChakraLink
                href={getDocumentationUrl(
                  appType as string,
                  appData.appPlatform as string
                )}
                isExternal
                color="blue.500"
                textDecoration="underline"
                className="x-small-font"
              >
                docs
              </ChakraLink>{' '}
              to integrate
            </Text>
            <Image
              src={AppVerify}
              mt="16px"
              className="dashbaoard-image"
              bgColor="white.700"
              alt="Illustration"
            />
            <HStack mt="32px">
              <Spinner boxSize={4} />
              <Text className="x-small-font" color="gray" mt="2px" fontFamily="inter">
                Waiting for the first proof from your application
              </Text>
            </HStack>
            <ChakraLink href="https://t.me/protocolreclaim" isExternal>
              <Button
                mt="8px"
                className="dashboard-button"
                _hover={{ bgColor: 'gray.800' }}
              >
                <Text fontFamily="inter" color="white" className="medium-font">
                  Get Help
                </Text>
              </Button>
            </ChakraLink>
          </VStack>
        </VStack>
        <VStack className="stepper-bar" alignItems="flex-start">
          <Flex alignItems="center" gap={3} justifyContent="space-between">
            <Button
              className="stepper-back-button small-font"
              _hover={{
                transform: 'scale(1.05)',
              }}
              onClick={handleBackAction}
            >
              Back
            </Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};
